import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import {
  convertRawChartToLineData as convertRawChartToLineDataBase,
  drawSpark,
  drawSparkSvg,
  getMovementToColour,
  getStatsToColour,
  ILineData,
  makeCanvasHeight,
  SparkContainer
} from './SparkBase';
import { PRICE_MOVEMENT, PriceMovement } from '../Delta/Delta';
import { BaseImg } from '../../common/BaseElements';

export interface ISpark2Props {
  data?: ILineData[];
  lineWidth?: 3 | 1 | 2 | 4;
  /**
   * @description A price line is a horizontal line drawn across the width of the chart at a specific price value
   */
  priceLineValue?: number;
  /**
   * @description Value between 0 and 1 that represent the horizontal width of the chart that is to be filled with data. The rest of the chart will be empty.
   */
  timeRange?: number;

  lastPriceMovement?: PriceMovement;
  useSVG?: boolean;
}

const imageSizeProps = (width: number): React.CSSProperties => {
  const height = makeCanvasHeight(width);
  return {
    width: `${width}px`,
    height: `${height}px`
  };
};

export const convertRawChartToLineData = (data: string[]) => convertRawChartToLineDataBase(data);

const canvasSizeProps = (width: number) => {
  const height = makeCanvasHeight(width);
  // use device pixel ratio to ensure canvas is sharp on high DPI screens
  const ratio = window.devicePixelRatio;
  return {
    width: width * ratio,
    height: height * ratio,
    style: {
      width: `${width}px`,
      height: `${height}px`
    }
  };
};

export const Spark2: React.FC<ISpark2Props> = ({ data = [], lineWidth = 3, priceLineValue, timeRange = 1, lastPriceMovement, useSVG = true, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'Spark2');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const [width, setWidth] = useState(containerRef.current?.clientWidth ?? 0);

  useEffect(() => {
    /* istanbul ignore next */
    if (containerRef.current === null) return;

    const resizeObserver = new ResizeObserver(
      /* istanbul ignore next */ () => {
        /* istanbul ignore next */
        setWidth(containerRef.current?.clientWidth ?? 0);
      }
    );

    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [containerRef?.current]);

  const colour = useMemo(() => {
    return lastPriceMovement && lastPriceMovement !== PRICE_MOVEMENT.NO_CHANGE && lastPriceMovement !== PRICE_MOVEMENT.UNKNOWN
      ? getMovementToColour(theme, lastPriceMovement)
      : getStatsToColour(priceLineValue, data, theme);
  }, [data, priceLineValue, lastPriceMovement, theme]);

  if (data.length === 0) return <></>;

  if (useSVG) {
    const svg = drawSparkSvg(width, data, colour, lineWidth, timeRange, priceLineValue);
    return (
      <SparkContainer ref={containerRef} {...generateTestId()} {...rest}>
        <BaseImg style={imageSizeProps(width)} src={`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`} />
      </SparkContainer>
    );
  }

  // TODO: Leaving here in case we need a quick rollback. Once everything is ok, remove this.
  drawSpark(canvasRef.current, width, data, colour, lineWidth, timeRange, priceLineValue);
  return (
    <SparkContainer ref={containerRef} {...generateTestId()} {...rest}>
      <canvas ref={canvasRef} {...canvasSizeProps(width)} />
    </SparkContainer>
  );
};
